import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

/**
 * Full-screen bootstrap / blocking loader. Shown until global data is ready
 * or while optional global load counter &gt; 0.
 */
function GlobalAppLoader({ visible }) {
    const reduceMotion = useReducedMotion();

    return (
        <AnimatePresence>
            {visible ? (
                <motion.div
                    key="global-app-loader"
                    className="global-app-loader"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="global-app-loader__glow" aria-hidden />
                    <div className="global-app-loader__mark">
                        <motion.div
                            className="global-app-loader__ring global-app-loader__ring--outer"
                            animate={reduceMotion ? { rotate: 0 } : { rotate: 360 }}
                            transition={
                                reduceMotion
                                    ? { duration: 0 }
                                    : {
                                          repeat: Infinity,
                                          duration: 1.35,
                                          ease: 'linear',
                                      }
                            }
                        >
                            <svg viewBox="0 0 120 120" fill="none" aria-hidden>
                                <defs>
                                    <linearGradient
                                        id="globalLoaderGradOuter"
                                        x1="0%"
                                        y1="0%"
                                        x2="100%"
                                        y2="100%"
                                    >
                                        <stop offset="0%" stopColor="#0041FF" />
                                        <stop offset="45%" stopColor="#6C3BFF" />
                                        <stop offset="100%" stopColor="#E6007E" />
                                    </linearGradient>
                                </defs>
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="52"
                                    stroke="url(#globalLoaderGradOuter)"
                                    strokeWidth="5"
                                    strokeLinecap="round"
                                    strokeDasharray="82 246"
                                />
                            </svg>
                        </motion.div>
                        <motion.div
                            className="global-app-loader__ring global-app-loader__ring--inner"
                            animate={reduceMotion ? { rotate: 0 } : { rotate: -360 }}
                            transition={
                                reduceMotion
                                    ? { duration: 0 }
                                    : {
                                          repeat: Infinity,
                                          duration: 2.1,
                                          ease: 'linear',
                                      }
                            }
                        >
                            <svg viewBox="0 0 120 120" fill="none" aria-hidden>
                                <defs>
                                    <linearGradient
                                        id="globalLoaderGradInner"
                                        x1="100%"
                                        y1="0%"
                                        x2="0%"
                                        y2="100%"
                                    >
                                        <stop offset="0%" stopColor="rgba(0, 65, 255, 0.95)" />
                                        <stop offset="100%" stopColor="rgba(230, 0, 126, 0.75)" />
                                    </linearGradient>
                                </defs>
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="36"
                                    stroke="url(#globalLoaderGradInner)"
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeDasharray="56 170"
                                    opacity="0.9"
                                />
                            </svg>
                        </motion.div>
                        <motion.div
                            className="global-app-loader__pulse"
                            animate={
                                reduceMotion
                                    ? { scale: 1, opacity: 0.45 }
                                    : {
                                          scale: [1, 1.18, 1],
                                          opacity: [0.35, 0.65, 0.35],
                                      }
                            }
                            transition={
                                reduceMotion
                                    ? { duration: 0 }
                                    : {
                                          repeat: Infinity,
                                          duration: 2.4,
                                          ease: 'easeInOut',
                                      }
                            }
                            aria-hidden
                        />
                        <motion.div
                            className="global-app-loader__center"
                            animate={reduceMotion ? { scale: 1 } : { scale: [1, 1.08, 1] }}
                            transition={
                                reduceMotion
                                    ? { duration: 0 }
                                    : {
                                          repeat: Infinity,
                                          duration: 1.5,
                                          ease: 'easeInOut',
                                      }
                            }
                            aria-hidden
                        >
                            <i className="fa-solid fa-film global-app-loader__icon" />
                        </motion.div>
                    </div>
                    <motion.p
                        className="global-app-loader__title"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.12, duration: 0.35 }}
                    >
                        Filmakee
                    </motion.p>
                    <p className="global-app-loader__subtitle">Loading your experience…</p>
                    <span className="global-app-loader__sr-only">Loading application data, please wait</span>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export default GlobalAppLoader;
